import React, { useState, useEffect } from "react";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../config/fbConfig";
import { collection, getDocs, query, where, addDoc, serverTimestamp, doc, getDoc, updateDoc } from "firebase/firestore";
import Loader from "./Loader";
import Modal from 'react-modal';
import SuccessModal from "./SuccessModal";
import Html5QrcodePlugin from './Html5QrcodePlugin';
import '../styles/Sales.css';
import generateReceiptPDF from './ReceiptGenerator';

Modal.setAppElement('#root');

const SalesEntry = () => {
  const { user } = useUserAuth();
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [printContent, setPrintContent] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [cashierFirstName, setCashierFirstName] = useState("");
  const [cashierLastName, setCashierLastName] = useState("");
  const [cashierPhone, setCashierPhone] = useState("");
  const [cashierStatus, setCashierStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [printerConnected, setPrinterConnected] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const activeProductsQuery = query(collection(db, "products"), where("isActive", "==", true));
        const activeProductsSnapshot = await getDocs(activeProductsQuery);
        const activeProducts = activeProductsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProducts(activeProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError("Failed to fetch products: " + error.message);
        setShowModal(true);
      }
    };

    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCashierFirstName(userData.firstName || "");
          setCashierLastName(userData.lastName || "");
          setCashierPhone(userData.phone || "");
          setCashierStatus(userData.status || "");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data: " + error.message);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    const checkPrinterConnection = async () => {
      try {
        const devices = await navigator.usb.getDevices();
        setPrinterConnected(devices.length > 0);
      } catch (err) {
        console.error("Error checking printer connection:", err);
        setPrinterConnected(false);
      }
    };

    fetchProducts();
    fetchUserData();
    checkPrinterConnection();
  }, [user.uid]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredProducts([]);
    } else {
      const lowerQuery = searchQuery.toLowerCase();
      const filtered = products.filter(
        (product) =>
          product.name.toLowerCase().includes(lowerQuery) ||
          product.category.toLowerCase().includes(lowerQuery) ||
          (product.barcode && typeof product.barcode === 'string' && product.barcode.toLowerCase().includes(lowerQuery))
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  useEffect(() => {
    if (selectedProduct) {
      const product = products.find((p) => p.id === selectedProduct);
      if (product) {
        if (product.quantity < 1) {
          setError(`Product ${product.name} is out of stock`);
          setShowModal(true);
          return;
        }
        setCart((prevCart) => {
          const existingItem = prevCart.find((item) => item.id === product.id);
          if (existingItem) {
            if (existingItem.quantity + 1 > product.quantity) {
              setError(`Cannot add more ${product.name}. Only ${product.quantity} in stock`);
              setShowModal(true);
              return prevCart;
            }
            return prevCart.map((item) =>
              item.id === product.id
                ? {
                    ...item,
                    quantity: item.quantity + 1,
                    subtotal: item.price * (item.quantity + 1),
                    totalCost: (item.costPrice || 0) * (item.quantity + 1),
                    profit: (item.price - (item.costPrice || 0)) * (item.quantity + 1)
                  }
                : item
            );
          } else {
            const costPrice = product.costPrice || 0;
            const newItem = { 
              ...product, 
              quantity: 1, 
              subtotal: product.price * 1,
              totalCost: costPrice * 1,
              profit: (product.price - costPrice) * 1
            };
            return [...prevCart, newItem];
          }
        });
        setSelectedProduct("");
        setSearchQuery("");
      }
    }
  }, [selectedProduct, products]);

  const onNewScanResult = (decodedText) => {
    setSearchQuery(decodedText);
    const product = products.find((p) => p.barcode && typeof p.barcode === 'string' && p.barcode.toLowerCase() === decodedText.toLowerCase());
    if (product) {
      setSelectedProduct(product.id);
    }
    setIsScanModalOpen(false);
  };

  const updateQuantity = (id, newQuantity) => {
    const quantity = newQuantity === "" ? "" : Math.max(1, parseInt(newQuantity) || 1);
    const product = products.find((p) => p.id === id);
    if (quantity > product.quantity) {
      setError(`Cannot set quantity to ${quantity}. Only ${product.quantity} ${product.name} in stock`);
      setShowModal(true);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) => {
        if (item.id === id) {
          const costPrice = item.costPrice || 0;
          const qty = quantity === "" ? 1 : quantity;
          return { 
            ...item, 
            quantity: qty, 
            subtotal: item.price * qty,
            totalCost: costPrice * qty,
            profit: (item.price - costPrice) * qty
          };
        }
        return item;
      })
    );
  };

  const incrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        const product = products.find((p) => p.id === item.id);
        if (item.id === id && item.quantity + 1 <= product.quantity) {
          const costPrice = item.costPrice || 0;
          const newQuantity = item.quantity + 1;
          return { 
            ...item, 
            quantity: newQuantity, 
            subtotal: item.price * newQuantity,
            totalCost: costPrice * newQuantity,
            profit: (item.price - costPrice) * newQuantity
          };
        } else if (item.id === id) {
          setError(`Cannot add more ${item.name}. Only ${product.quantity} in stock`);
          setShowModal(true);
          return item;
        }
        return item;
      })
    );
  };

  const decrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        if (item.id === id && item.quantity > 1) {
          const costPrice = item.costPrice || 0;
          const newQuantity = item.quantity - 1;
          return { 
            ...item, 
            quantity: newQuantity, 
            subtotal: item.price * newQuantity,
            totalCost: costPrice * newQuantity,
            profit: (item.price - costPrice) * newQuantity
          };
        }
        return item;
      })
    );
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.subtotal, 0);
  };

  const calculateTotalCost = () => {
    return cart.reduce((total, item) => total + (item.totalCost || 0), 0);
  };

  const calculateTotalProfit = () => {
    return cart.reduce((total, item) => total + (item.profit || 0), 0);
  };

  const wrapText = (text, maxWidth) => {
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxWidth) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines.map(line => {
      const padding = Math.floor((maxWidth - line.length) / 2);
      return ' '.repeat(padding > 0 ? padding : 0) + line;
    }).join('\n');
  };

  const saveSale = async () => {
    if (cart.length === 0) return;
    try {
      // Validate stock before saving
      for (const item of cart) {
        const product = products.find((p) => p.id === item.id);
        if (item.quantity > product.quantity) {
          throw new Error(`Insufficient stock for ${item.name}. Available: ${product.quantity}`);
        }
      }

      // Prepare products array with all required fields
      const productsArray = cart.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        total: item.subtotal,
        unitPrice: item.price,
        costPrice: item.costPrice || 0,
        totalCostPrice: (item.costPrice || 0) * item.quantity,
        profit: item.subtotal - ((item.costPrice || 0) * item.quantity)
      }));

      const cashierName = `${cashierFirstName} ${cashierLastName}`.trim() || user.email || 'Unknown';
      const totalProfit = calculateTotalProfit();
      const totalCost = calculateTotalCost();
      const grandTotal = calculateTotal();
      
      // Save sale to Firestore with all required fields
      await addDoc(collection(db, "sales"), {
        userId: user.uid,
        cashierName,
        cashierPhone: cashierPhone || '',
        cashierStatus: cashierStatus || '',
        timestamp: serverTimestamp(),
        customerName: customerName || '',
        customerPhone: customerPhone || '',
        products: productsArray,
        grandTotal: grandTotal,
        totalCost: totalCost,
        totalProfit: totalProfit,
        profitMargin: grandTotal > 0 ? (totalProfit / grandTotal) * 100 : 0
      });

      // Update product quantities
      for (const item of cart) {
        const productRef = doc(db, "products", item.id);
        const product = products.find((p) => p.id === item.id);
        await updateDoc(productRef, {
          quantity: product.quantity - item.quantity
        });
      }

      // Update local products state
      setProducts((prevProducts) =>
        prevProducts.map((product) => {
          const cartItem = cart.find((item) => item.id === product.id);
          if (cartItem) {
            return { ...product, quantity: product.quantity - cartItem.quantity };
          }
          return product;
        })
      );

      setCart([]);
      setCustomerName("");
      setCustomerPhone("");
      setSuccessMessage("Sale saved successfully");
      setShowModal(true);
    } catch (error) {
      console.error("Error saving sale:", error);
      setError("Failed to save sale: " + error.message);
      setShowModal(true);
    }
  };

  const handlePrint = async () => {
    try {
      const pdfDoc = generateReceiptPDF(
        cart, 
        user, 
        cashierFirstName, 
        cashierLastName, 
        customerName, 
        customerPhone,
        new Date()
      );
      pdfDoc.save(`Receipt_${new Date().toISOString().replace(/[:.]/g, '-')}.pdf`);
      saveSale();
    } catch (error) {
      setError(error.message);
      setShowModal(true);
    }
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  return (
    <div className="sales-entry-container padding-bottom-d">
      <div className="row align-center space-between sales-entry-head">
        <p className="caps login-heading page-heading page-heading-d">SALES ENTRY</p>
        <div className="row space-between align-center gap-15">
          <button onClick={handlePrint} className="list-btn blue-back-btn sales-btn-desktop">PRINT</button>
          <button onClick={saveSale} className="list-btn blue-back-btn sales-btn-desktop">SAVE</button>
        </div>
      </div>
      <div className="sales-entry">
        <div className="sales-entry-input-fields">
          <div className="input-group">
            <div className="row align-start sales-search-scan-field gap-10" style={{position: 'relative'}}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field1 input-field1-desktop"
                placeholder="Search by product name, category or barcode"
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchQuery("")}                  
                >
                  <i className="fas fa-times font-red"></i>
                </button>
              )}
              <button
                className="list-btn scan-btn"
                onClick={() => setIsScanModalOpen(true)}
              >
                <i className="fas fa-barcode"></i>
              </button>
            </div>
            {filteredProducts.length > 0 && (
              <ul className="product-dropdown">
                {filteredProducts.map((product) => (
                  <li
                    key={product.id}
                    onClick={() => setSelectedProduct(product.id)}
                    className="product-dropdown-item"
                  >
                    {product.name} (₦ {product.price})<br />
                    {product.quantity && `Quantity: ${product.quantity}`} {product.category && `| Category: ${product.category}`}                    
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="customer-details">
            <div className="row gap-10">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="input-field1"
                placeholder="Name"
              />
              <input
                type="text"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="input-field1"
                placeholder="Phone Number"
              />
            </div>
          </div>
        </div>
        <div className="cart scroll-fixed5">        
          {cart.length > 0 ? (
            <table>
              <thead>
                <tr style={{ backgroundColor: '#f4f4f4' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Product/Quantity</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', textAlign: 'left' }}>
                      <span>{item.name}</span>
                      <div className="row align-center gap-10">
                        <button
                          onClick={() => decrementQuantity(item.id)}
                          className="quantity-button"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, e.target.value)}
                          className="quantity-input"
                          min="1"
                        />
                        <button
                          onClick={() => incrementQuantity(item.id)}
                          className="quantity-button"
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className="row space-between" style={{ padding: '12px', textAlign: 'right' }}>
                      <span>₦ {item.subtotal.toLocaleString()}</span>
                      <button onClick={() => removeFromCart(item.id)} className="remove-button">
                        <i className="fas fa-trash-alt font-red"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="space-between" colSpan="2" style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                    <span>Grand Total</span>
                    <span>₦ {calculateTotal().toLocaleString()}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <p style={{ textAlign: 'center', marginTop: '20px' }}>No items in cart</p>
          )}
        </div>
      </div>
      {printContent && (
        <div className="print-section" style={{ display: 'none' }}>
          <pre>{printContent}</pre>
        </div>
      )}
      <Modal
        isOpen={isScanModalOpen}
        onRequestClose={() => setIsScanModalOpen(false)}
        contentLabel="Scan Barcode"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Scan Barcode</h2>
        <Html5QrcodePlugin
          fps={10}
          qrbox={250}
          disableFlip={false}
          qrCodeSuccessCallback={onNewScanResult}
          autoStart={true}
        />
        <button className="login-button" onClick={() => setIsScanModalOpen(false)}>Close Scanner</button>
      </Modal>
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
};

export default SalesEntry;